import BenchlingConfig from 'BenchlingConfig';
import staticURL from 'lib/staticURL';

// Constants for known models (for hardcoded references in code)
export const SCIENTIFIC_MODEL = {
  ALPHAFOLD: 'alphafold', // This is called AlphaFold in the code, but AlphaFold2 in the UI
  CHAI1: 'chai1',
  BOLTZ2: 'boltz2',
  OPENFOLD2: 'openfold2', // This is called OpenFold2 in the code, but OpenFold in the UI
  OPENFOLD3: 'openfold3',
  PROTENIX: 'protenix',
  BOLTZGEN: 'boltzgen',
} as const;

// Frontend constants for scientific models
// Keep in sync with StructurePredictionModel in benchling/molbio/aa_sequences/models/structure_prediction_run.py

const MIN_AMINO_ACID_COUNT = 1;
const MAX_AMINO_ACID_COUNT = 1500;

// Keep in sync with MAX_PREDICTION_JOBS_PER_PREDICTION_BATCH in benchling/model_hub/connectors/prediction_batch_connector.py
export const MAX_BULK_PREDICTION_ITEMS = 100;

// Maximum number of components allowed in a multimer prediction
// Keep in sync with MAX_MULTIMER_COMPONENTS in benchling/molbio/aa_sequences/structure_prediction/constants.py
const MAX_MULTIMER_COMPONENTS = 25;

// Keep in sync with MAX_CHAI1_TOKEN_COUNT in benchling/molbio/aa_sequences/structure_prediction/constants.py
const MAX_CHAI1_TOKEN_COUNT = 2048;

/**
 * Check if a model string is a known/valid model.
 */
export function isValidModel(model: string): boolean {
  return Object.hasOwn(SCIENTIFIC_MODEL_CONFIG, model);
}

const MODEL_CATEGORY = {
  STRUCTURE_PREDICTION: 'structure_prediction',
  GENERATIVE: 'generative',
};

export const MSA_SUPPORT = {
  NONE: 'NONE',
  OPTIONAL: 'OPTIONAL',
  REQUIRED: 'REQUIRED',
} as const;

export interface ScientificModelConfig {
  category: (typeof MODEL_CATEGORY)[keyof typeof MODEL_CATEGORY];
  // Display & UI
  displayName: string;
  description: string;
  iconUrl: string;
  externalLink: string;
  paperLink?: string; // Optional link to the research paper (falls back to externalLink if not provided)

  // Capabilities
  supportsMultimers: boolean;
  supportsMulitmerMolecules: boolean;
  msaSupport: (typeof MSA_SUPPORT)[keyof typeof MSA_SUPPORT];
  showMSARecentlyAddedNotice: boolean;
  egressesData: boolean;
  supportsBulkPredictions: boolean;

  // Limits (null means not applicable)
  minAminoAcidCount: number | null;
  maxAminoAcidCount: number | null;
  maxTokenCount: number | null;
  maxMultimerComponents: number | null;

  // Feature flags & availability
  requiresFeatureFlags: Array<keyof typeof BenchlingConfig>; // e.g., 'MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS'
  isPreview: boolean;
}

/**
 * Unified configuration for all scientific models.
 * This is the single source of truth for all model-specific data.
 */
export const SCIENTIFIC_MODEL_CONFIG: Record<string, ScientificModelConfig> = {
  [SCIENTIFIC_MODEL.ALPHAFOLD]: {
    category: MODEL_CATEGORY.STRUCTURE_PREDICTION,
    displayName: 'AlphaFold2',
    description:
      'Predict protein structures from amino acid sequences. Supports monomers and multimers with high accuracy for well-characterized protein families. Developed by Google DeepMind.',
    iconUrl: staticURL('/static/img/model_hub/alphafold2.png'),
    externalLink: 'https://deepmind.google/science/alphafold/',
    paperLink: 'https://www.nature.com/articles/s41586-021-03819-2',
    supportsMultimers: false,
    supportsMulitmerMolecules: false,
    msaSupport: MSA_SUPPORT.REQUIRED,
    showMSARecentlyAddedNotice: false,
    egressesData: false,
    supportsBulkPredictions: false,
    minAminoAcidCount: MIN_AMINO_ACID_COUNT,
    maxAminoAcidCount: MAX_AMINO_ACID_COUNT,
    maxTokenCount: null,
    maxMultimerComponents: null,
    requiresFeatureFlags: ['MOLBIO_ENABLE_ALPHAFOLD_GA'],
    isPreview: false,
  },
  [SCIENTIFIC_MODEL.CHAI1]: {
    category: MODEL_CATEGORY.STRUCTURE_PREDICTION,
    displayName: 'Chai-1',
    description:
      'Multi-modal structure prediction for proteins, small molecules, and molecular complexes. Supports joint prediction of protein-ligand interactions. Developed by Chai Discovery.',
    iconUrl: staticURL('/static/img/model_hub/chai1.png'),
    externalLink: 'https://www.chaidiscovery.com/news/introducing-chai-1',
    supportsMultimers: true,
    supportsMulitmerMolecules: true,
    msaSupport: MSA_SUPPORT.NONE,
    showMSARecentlyAddedNotice: false,
    egressesData: false,
    supportsBulkPredictions: true,
    minAminoAcidCount: MIN_AMINO_ACID_COUNT,
    maxAminoAcidCount: MAX_AMINO_ACID_COUNT,
    maxTokenCount: MAX_CHAI1_TOKEN_COUNT,
    maxMultimerComponents: MAX_MULTIMER_COMPONENTS,
    requiresFeatureFlags: ['MOLBIO_ENABLE_ALPHAFOLD_GA', 'MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS'],
    isPreview: false,
  },
  [SCIENTIFIC_MODEL.BOLTZ2]: {
    category: MODEL_CATEGORY.STRUCTURE_PREDICTION,
    displayName: 'Boltz-2',
    description:
      'Multi-modal structure prediction. Jointly models protein structures, small molecules, and molecular complexes. Developed at the MIT Jameel Clinic.',
    iconUrl: staticURL('/static/img/model_hub/boltz2.png'),
    externalLink: 'https://boltz.bio/boltz2',
    supportsMultimers: true,
    supportsMulitmerMolecules: true,
    msaSupport: MSA_SUPPORT.NONE,
    showMSARecentlyAddedNotice: false,
    egressesData: false,
    supportsBulkPredictions: true,
    minAminoAcidCount: MIN_AMINO_ACID_COUNT,
    maxAminoAcidCount: MAX_AMINO_ACID_COUNT,
    maxTokenCount: MAX_CHAI1_TOKEN_COUNT,
    maxMultimerComponents: MAX_MULTIMER_COMPONENTS,
    requiresFeatureFlags: ['MOLBIO_ENABLE_ALPHAFOLD_GA', 'MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS'],
    isPreview: false,
  },
  [SCIENTIFIC_MODEL.PROTENIX]: {
    category: MODEL_CATEGORY.STRUCTURE_PREDICTION,
    displayName: 'Protenix-v1',
    description:
      'Multi-modal structure prediction for proteins and biomolecular complexes. Developed by ByteDance Research.',
    iconUrl: staticURL('/static/img/model_hub/protenix.png'),
    externalLink: 'https://github.com/bytedance/Protenix',
    paperLink: 'https://www.biorxiv.org/content/10.64898/2026.02.05.703733v1',
    supportsMultimers: true,
    supportsMulitmerMolecules: true,
    msaSupport: MSA_SUPPORT.OPTIONAL,
    showMSARecentlyAddedNotice: false,
    egressesData: false,
    supportsBulkPredictions: true,
    minAminoAcidCount: MIN_AMINO_ACID_COUNT,
    maxAminoAcidCount: MAX_AMINO_ACID_COUNT,
    maxTokenCount: MAX_CHAI1_TOKEN_COUNT,
    maxMultimerComponents: MAX_MULTIMER_COMPONENTS,
    requiresFeatureFlags: [
      'MOLBIO_ENABLE_ALPHAFOLD_GA',
      'MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS',
      'MODEL_HUB_ENABLE_PROTENIX',
    ],
    isPreview: false,
  },
  [SCIENTIFIC_MODEL.BOLTZGEN]: {
    category: MODEL_CATEGORY.GENERATIVE,
    displayName: 'BoltzGen',
    description:
      'Generate novel protein sequences designed to bind to specified target Amino Acid sequences. Developed at the MIT Jameel Clinic.',
    iconUrl: staticURL('/static/img/model_hub/boltz2.png'),
    externalLink: 'https://boltz.bio/boltzgen',
    supportsMultimers: false,
    supportsMulitmerMolecules: false,
    msaSupport: MSA_SUPPORT.NONE,
    showMSARecentlyAddedNotice: false,
    egressesData: false,
    supportsBulkPredictions: false,
    minAminoAcidCount: null,
    maxAminoAcidCount: null,
    maxTokenCount: null,
    maxMultimerComponents: null,
    requiresFeatureFlags: ['MODEL_HUB_ENABLE_GENERATIVE_MODELS_TAB'],
    isPreview: true,
  },
  [SCIENTIFIC_MODEL.OPENFOLD2]: {
    category: MODEL_CATEGORY.STRUCTURE_PREDICTION,
    displayName: 'OpenFold',
    description:
      "Protein structure prediction model reproducing AlphaFold2's approach, with additional optimizations (e.g. speed). Currently supports monomer prediction from amino acid sequences. Developed by the OpenFold Consortium.",
    iconUrl: staticURL('/static/img/model_hub/openfold2.png'),
    externalLink: 'https://www.biorxiv.org/content/10.1101/2022.11.20.517210v3',
    supportsMultimers: false,
    supportsMulitmerMolecules: false,
    msaSupport: MSA_SUPPORT.OPTIONAL,
    showMSARecentlyAddedNotice: false,
    egressesData: false,
    supportsBulkPredictions: true,
    minAminoAcidCount: 16,
    maxAminoAcidCount: MAX_AMINO_ACID_COUNT,
    maxTokenCount: null,
    maxMultimerComponents: MAX_MULTIMER_COMPONENTS,
    requiresFeatureFlags: [
      'MOLBIO_ENABLE_ALPHAFOLD_GA',
      'MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS',
      'MODEL_HUB_ENABLE_OPENFOLD2',
    ],
    isPreview: false,
  },
  [SCIENTIFIC_MODEL.OPENFOLD3]: {
    category: MODEL_CATEGORY.STRUCTURE_PREDICTION,
    displayName: 'OpenFold3',
    description:
      "Protein structure prediction model reproducing AlphaFold3's approach to multi-modal. Currently supports monomer prediction from amino acid sequences. Developed by the OpenFold Consortium.",
    iconUrl: staticURL('/static/img/model_hub/openfold2.png'),
    externalLink: 'https://github.com/aqlaboratory/openfold-3',
    supportsMultimers: false,
    supportsMulitmerMolecules: false,
    msaSupport: MSA_SUPPORT.OPTIONAL,
    showMSARecentlyAddedNotice: false,
    egressesData: false,
    supportsBulkPredictions: true,
    minAminoAcidCount: 16,
    maxAminoAcidCount: MAX_AMINO_ACID_COUNT,
    maxTokenCount: null,
    maxMultimerComponents: MAX_MULTIMER_COMPONENTS,
    requiresFeatureFlags: [
      'MOLBIO_ENABLE_ALPHAFOLD_GA',
      'MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS',
      'MODEL_HUB_ENABLE_OPENFOLD3',
    ],
    isPreview: false,
  },
};

/**
 * Get all models that are currently available based on feature flags.
 */
export function getAvailableStructurePredictionModels(): Array<string> {
  return Object.keys(SCIENTIFIC_MODEL_CONFIG)
    .filter((model) => getModelConfig(model).category === MODEL_CATEGORY.STRUCTURE_PREDICTION)
    .filter((model) => {
      const config = getModelConfig(model);
      if (config.requiresFeatureFlags.length > 0) {
        return config.requiresFeatureFlags.every(
          (featureFlag: keyof typeof BenchlingConfig) =>
            (BenchlingConfig as Record<string, unknown>)[featureFlag] === true
        );
      }
      return true;
    });
}

/**
 * Get the default model based on feature flags.
 * When MODEL_HUB_ENABLE_SCIENTIFIC_MODELS is enabled, returns BOLTZ2.
 * Otherwise, returns ALPHAFOLD.
 */
export function getDefaultStructurePredictionModel(): string {
  return BenchlingConfig.MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS
    ? SCIENTIFIC_MODEL.BOLTZ2
    : SCIENTIFIC_MODEL.ALPHAFOLD;
}

/**
 * Returns the first candidate model that is currently available, falling back to the
 * flag-based default. Use this to resolve the initial model selection when multiple
 * sources of truth exist (e.g. a prior run's model, a stored user preference, and a default).
 */
export function resolveInitialStructurePredictionModel(
  availableModels: Array<string>,
  ...candidates: Array<string | undefined>
): string {
  for (const candidate of candidates) {
    if (candidate != null && availableModels.includes(candidate)) {
      return candidate;
    }
  }
  return getDefaultStructurePredictionModel();
}

export enum ScientificModelPreferences {
  HAS_DISMISSED_MODEL_CONFIDENCE_POPOVER = 'HAS_DISMISSED_MODEL_CONFIDENCE_POPOVER',
  LAST_SELECTED_STRUCTURE_PREDICTION_MODEL = 'LAST_SELECTED_STRUCTURE_PREDICTION_MODEL',
}

/**
 * Get the complete configuration for a structure prediction model.
 * @throws Error if the model is not found in the configuration.
 */
export function getModelConfig(model: string): ScientificModelConfig {
  const config = SCIENTIFIC_MODEL_CONFIG[model];
  if (!config) {
    throw new Error(`Unknown scientific model: ${model}`);
  }
  return config;
}

/**
 * Get the user-friendly display name for a structure prediction model.
 */
export function getModelDisplayName(model: string): string {
  const config = getModelConfig(model);
  return config.displayName;
}

export function getModelIconUrl(model: string): string {
  const config = getModelConfig(model);
  return config.iconUrl;
}

/**
 * Get the external link/documentation URL for a model.
 */
export function getModelExternalLink(model: string): string {
  const config = getModelConfig(model);
  return config.externalLink;
}

/**
 * Get the paper link for a model (falls back to externalLink if paperLink is not provided).
 */
export function getModelPaperLink(model: string): string {
  const config = getModelConfig(model);
  return config.paperLink ?? config.externalLink;
}

/**
 * Check if a model supports multimer predictions.
 */
export function supportsMultimers(model: string): boolean {
  const config = getModelConfig(model);
  return config.supportsMultimers;
}

/**
 * Check if a model supports multimer molecules.
 */
export function supportsMulitmerMolecules(model: string): boolean {
  const config = getModelConfig(model);
  return config.supportsMulitmerMolecules;
}

/**
 * Check if a model supports MSA inputs for structure prediction accuracy.
 */
export function supportsMSA(model: string): boolean {
  const config = getModelConfig(model);
  return config.msaSupport !== MSA_SUPPORT.NONE;
}

/**
 * Check if users can choose whether to use MSAs for a model.
 */
export function isMSAConfigurable(model: string): boolean {
  const config = getModelConfig(model);
  return config.msaSupport === MSA_SUPPORT.OPTIONAL;
}

/**
 * Get the MSA value to send when submitting a prediction.
 * REQUIRED models always use MSAs; OPTIONAL models follow the user's toggle; NONE omits the flag (null).
 */
export function getUseMSAForSubmission(model: string, msaEnabled: boolean): boolean | null {
  const config = getModelConfig(model);
  if (config.msaSupport === MSA_SUPPORT.REQUIRED) {
    return true;
  }
  if (config.msaSupport === MSA_SUPPORT.OPTIONAL) {
    return msaEnabled;
  }
  return null;
}

/**
 * Check if the UI should call out that MSA support was recently added.
 */
export function shouldShowMSARecentlyAddedNotice(model: string): boolean {
  const config = getModelConfig(model);
  return config.showMSARecentlyAddedNotice;
}

/**
 * Get all models that support MSA inputs.
 */
export function getModelsWithMSASupport(): Array<string> {
  return Object.keys(SCIENTIFIC_MODEL_CONFIG).filter((model) => supportsMSA(model));
}

/**
 * Check if a model supports bulk predictions.
 */
export function supportsBulkPredictions(model: string): boolean {
  const config = getModelConfig(model);
  return config.supportsBulkPredictions;
}

/**
 * Check if a model is in preview mode.
 */
export function isPreviewModel(model: string): boolean {
  const config = getModelConfig(model);
  return config.isPreview ?? false;
}

/**
 * Check if a model egresses data to third parties.
 */
export function modelEgressesData(model: string): boolean {
  const config = getModelConfig(model);
  return config.egressesData ?? false;
}

/**
 * Get the minimum amino acid count required for structure predictions.
 * Returns null if the model doesn't have a minimum requirement.
 */
export function getMinAminoAcidCount(model: string): number {
  const config = getModelConfig(model);
  return config.minAminoAcidCount ?? MIN_AMINO_ACID_COUNT;
}

/**
 * Get the maximum amino acid count allowed for structure predictions.
 * Currently all models share the same limit.
 */
export function getMaxAminoAcidCount(model: string): number {
  const config = getModelConfig(model);
  return config.maxAminoAcidCount ?? MAX_AMINO_ACID_COUNT;
}

/**
 * Get the maximum number of components allowed in a multimer prediction.
 * Currently all models share the same limit.
 */
export function getMaxMultimerComponents(model: string): number {
  const config = getModelConfig(model);
  if (!config.supportsMultimers) {
    return 1;
  }
  return config.maxMultimerComponents ?? MAX_MULTIMER_COMPONENTS;
}

/**
 * Get the maximum token count allowed for a model.
 * Returns null if the model doesn't have a token limit.
 */
export function getMaxTokenCount(model: string): number | null {
  const config = getModelConfig(model);
  return config.maxTokenCount ?? null;
}
